import { useState } from "react"
import { Button, Checkbox } from "../../../components"
import { MESSAGES } from "../constants/messages"

export const ProcessedDataTable = ({ 
  csvData, 
  csvErrors, 
  onClearData, 
  getTotalQuantity, 
  getTotalCost,
  onSave, 
  isDataValid,
  isSaving = false,
  onConfigureProduct = () => {}
}) => {
  const [showErrorsOnly, setShowErrorsOnly] = useState(false)
  
  const totalItems = csvData.length + csvErrors.length
  const errorCount = csvErrors.length
  
  if (totalItems === 0) return null

  // Combinar dados válidos e dados com erro para exibição
  const allData = [
    ...csvData.map(item => ({ ...item, hasError: false, error: null, isProductNotFound: false })),
    ...csvErrors.map(item => ({
      sku: item.sku || '', 
      quantity: item.quantity || '', 
      cost: item.cost != null ? item.cost : '',
      lineNumber: item.lineNumber,
      hasError: true, 
      error: item.error,
      isProductNotFound: item.isProductNotFound || false
    }))
  ].sort((a, b) => a.lineNumber - b.lineNumber)

  // Filtrar dados se necessário
  const displayData = showErrorsOnly ? allData.filter(item => item.hasError) : allData

  // Função para renderizar erros em múltiplas linhas
  const renderError = (errorText) => {
    if (!errorText) return '-'
    
    // Se o erro contém '; ' significa que há múltiplos erros
    if (errorText.includes('; ')) {
      const errors = errorText.split('; ')
      return (
        <div className="space-y-1">
          {errors.map((error, index) => (
            <div key={index} className="text-xs">
              • {error.trim()}
            </div>
          ))}
        </div>
      )
    }
    
    return errorText
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Dados Processados ({totalItems} itens)
            {errorCount > 0 && (
              <span className="text-red-600"> ({errorCount} Erros)</span>
            )}
          </h3>
          
          {errorCount > 0 && (
            <Checkbox
              label="Mostrar apenas erros"
              checked={showErrorsOnly}
              onChange={(checked) => setShowErrorsOnly(checked)}
            />
          )}
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={onClearData}
            type="secondary"
            disabled={isSaving}
          >
            {MESSAGES.BUTTONS.clearData}
          </Button>
          
          <Button 
            onClick={onSave}
            disabled={!isDataValid || isSaving}
            loading={isSaving}
            loading_label="Validando..."
            className={`text-sm px-4 py-2 ${
              (!isDataValid || isSaving)
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700'
            } text-white`}
          >
            {MESSAGES.BUTTONS.saveReceiving}
          </Button>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                Linha
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                SKU
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quantidade
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Custo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Erros
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayData.map((item, index) => (
              <tr 
                key={`${item.lineNumber}-${index}`} 
                className={`${item.hasError ? 'bg-red-50' : 'hover:bg-gray-50'}`}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.lineNumber}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.sku}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.quantity}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.cost !== undefined && item.cost !== null && item.cost !== '' ? `R$ ${parseFloat(item.cost).toFixed(2)}` : '-'}
                </td>
                <td className={`px-6 py-4 text-sm ${
                  item.hasError ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {renderError(item.error)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.hasError && item.isProductNotFound && (
                    <Button
                      onClick={() => onConfigureProduct(item.sku, item.cost)}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1"
                    >
                      Configurar Produto
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-4 text-sm text-gray-500">
        {showErrorsOnly ? (
          <>Mostrando {displayData.length} erro(s) de {totalItems} itens totais</>
        ) : (
          <>
            Total de itens: {totalItems} | 
            Itens válidos: {csvData.length} | 
            Quantidade total válida: {getTotalQuantity()} | 
            Custo total válido: R$ {getTotalCost ? getTotalCost().toFixed(2) : '0.00'}
            {errorCount > 0 && <span className="text-red-600"> | Erros: {errorCount}</span>}
          </>
        )}
      </div>
    </div>
  )
}